import { Link } from "react-router-dom";
import { Send, CalendarClock, FileLock2, UserRound, Video } from "lucide-react";
import { PageHeader, StatusBadge } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { useFetch, fmtDate } from "@/lib/api";

const APP_TEXT = {
  Pending: "Our recruiters are reviewing your application. We'll email you once it's approved.",
  Approved: "Your profile is live in our talent directory. Businesses can now send you offers.",
  Declined: "Your application wasn't approved this time. Keep your profile and documents up to date.",
};

export default function CandidateHome() {
  const { user } = useAuth();
  const { data: me } = useFetch("/me/candidate");
  const { data: offers } = useFetch("/offers");
  const { data: ints } = useFetch("/me/interviews");
  const { data: reqs } = useFetch("/doc-requests");
  const status = me?.application_status || "Approved";
  const pending = (offers || []).filter((o) => o.status === "Pending").length;
  const openReqs = (reqs?.incoming || []).filter((r) => r.status === "Open").length;

  const cards = [
    [Send, "Offers awaiting reply", pending, "/candidate/offers", "cand-stat-offers"],
    [CalendarClock, "Interviews scheduled", (ints || []).length, "/candidate", "cand-stat-interviews"],
    [FileLock2, "File requests open", openReqs, "/candidate/documents", "cand-stat-requests"],
    [UserRound, "Licenses on profile", (me?.licenses || []).length, "/candidate/profile", "cand-stat-licenses"],
  ];

  return (
    <div>
      <PageHeader eyebrow="Candidate portal" title={`Hi, ${user?.name?.split(" ")[0]}`} desc="Track your application, offers and documents." />
      <div data-testid="application-status-card" className="mb-8 rounded-md border border-stone-200 bg-white p-6">
        <div className="flex items-center gap-3"><p className="font-display text-lg font-bold">Application status</p><StatusBadge status={status} testId="application-status" /></div>
        <p className="mt-2 text-sm text-stone-600">{APP_TEXT[status]}</p>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map(([Icon, label, v, to, tid]) => (
          <Link key={label} to={to} data-testid={tid} className="lift rounded-md border border-stone-200 bg-white p-6">
            <Icon className="h-5 w-5 text-stone-500" strokeWidth={1.5} />
            <p className="mt-6 font-display text-4xl font-extrabold">{v ?? "–"}</p>
            <p className="mt-1 text-xs font-mono uppercase tracking-wider text-stone-500">{label}</p>
          </Link>
        ))}
      </div>
      <h2 className="mb-4 mt-12 font-display text-2xl font-bold">Upcoming interviews</h2>
      {(ints || []).length === 0 && <p className="text-sm text-stone-500">No interviews scheduled yet.</p>}
      <div className="space-y-3">
        {(ints || []).map((i) => (
          <div key={i.id} data-testid={`cand-interview-${i.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-stone-200 bg-white p-5">
            <div><p className="font-medium">{i.business.name}</p><p className="text-sm text-stone-500">{fmtDate(i.scheduled_at)}</p></div>
            <div className="flex items-center gap-3">
              <StatusBadge status={i.status} />
              {i.meeting_link && <a href={i.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium underline"><Video className="h-4 w-4" />Join</a>}
              <Link to={`/candidate/businesses/${i.business_id}`} className="text-sm text-stone-600 underline">View business</Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
